import React, { useCallback, useEffect, useState } from "react";

// Disbursement reconciliation (ADR-002 / Eclectics).
// Payouts the system could not PROVE the outcome of land here. A human
// confirms them as PAID (with the M-Pesa / provider reference) or NOT_PAID.
// There is deliberately no "retry" button: resending outside the TSQ rules
// is how a recipient gets paid twice.

const API = process.env.REACT_APP_API_BASE_URL ?? "";
const BASE = `${API}/api/v2/cockpit/disbursements`;
const getToken = () => localStorage.getItem("adminToken");
const REFRESH_MS = 60 * 1000;

async function call(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON */ }
  if (res.status === 401) throw new Error("Your cockpit session has expired. Please log in again.");
  return { status: res.status, body: body || {} };
}

function fmtAge(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function fmtAmount(amount, currency) {
  const n = Number(amount);
  return Number.isFinite(n) ? `${currency || ""} ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim() : "—";
}

function fmtTime(t) {
  return t ? new Date(t).toLocaleString() : "—";
}

const NAME_BADGE = {
  MATCH: { cls: "bg-green-100 text-green-800", label: "Name matches" },
  PARTIAL: { cls: "bg-amber-100 text-amber-800", label: "Partial name match" },
  MISMATCH: { cls: "bg-red-100 text-red-800", label: "Name does NOT match" },
  UNKNOWN: { cls: "bg-gray-100 text-gray-700", label: "No name reported" },
};

function ProviderStatus() {
  const [p, setP] = useState(null);
  useEffect(() => {
    call("/providers").then(({ body }) => { if (body.ok) setP(body); }).catch(() => {});
  }, []);
  if (!p) return null;
  const mode = p.modes?.REMITTANCE?.mode || "UNKNOWN";
  const auth = p.remittance?.auth;
  const modeCls = mode === "LIVE" ? "bg-red-100 text-red-800" : mode === "SANDBOX" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700";
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm bg-white border rounded-lg px-4 py-3">
      <span className="font-medium">Kenya payouts:</span>
      <span>{p.remittance?.provider || "none registered"}</span>
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${modeCls}`}>{mode}</span>
      {auth && (
        <span className="text-gray-600">
          {auth.hasToken ? `Token valid until ${fmtTime(auth.expiresAt)}` : "No active token (fetched on first use)"}
        </span>
      )}
    </div>
  );
}

function ItemDetail({ id, onClose, onResolved }) {
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'PAID' | 'NOT_PAID'
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(() => {
    setError(null);
    call(`/items/${id}`)
      .then(({ status, body }) => (body.ok ? setItem(body.item) : setError(body.error || `HTTP ${status}`)))
      .catch((e) => setError(e.message));
  }, [id]);

  useEffect(() => { load(); setMode(null); setReference(""); setNote(""); setFlash(null); }, [load]);

  const submit = async () => {
    const summary = mode === "PAID"
      ? `Confirm ${item.itemReference} WAS PAID with reference ${reference.trim()}?\n\nThe order will be marked successful.`
      : `Confirm ${item.itemReference} was NOT paid?\n\nThe item will be marked failed and will NOT be retried automatically.`;
    if (!window.confirm(summary)) return;
    setSaving(true); setFlash(null);
    try {
      const { status, body } = await call(`/items/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ outcome: mode, providerReference: reference.trim() || undefined, note: note.trim() }),
      });
      if (body.ok) {
        setFlash({ ok: true, text: `Resolved as ${body.resolution}.` });
        setMode(null);
        load();
        onResolved();
      } else {
        setFlash({ ok: false, text: body.error || `HTTP ${status}` });
        if (status === 409) { load(); onResolved(); }
      }
    } catch (e) {
      setFlash({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="bg-white border rounded-lg p-4 text-red-700">{error}</div>;
  if (!item) return <div className="bg-white border rounded-lg p-4 text-gray-500">Loading…</div>;

  const badge = NAME_BADGE[item.nameCheck?.result] || NAME_BADGE.UNKNOWN;
  const noteOk = note.trim().length >= 10;
  const refOk = mode !== "PAID" || reference.trim().length >= 4;

  return (
    <div className="bg-white border rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">{item.itemReference}</h2>
          <p className="text-sm text-gray-500">Order {item.orderReference || "—"} · status {item.status}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-sm">Close ✕</button>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <dt className="text-gray-500">Amount</dt><dd className="font-medium">{fmtAmount(item.destinationAmount, item.destinationCurrency)}</dd>
        <dt className="text-gray-500">Recipient</dt><dd>{item.recipient?.name || "—"} · {item.recipient?.phone || "—"}</dd>
        <dt className="text-gray-500">Provider</dt><dd>{item.provider || "—"}</dd>
        <dt className="text-gray-500">Our reference</dt><dd className="font-mono">{item.requestRefNum || "—"}</dd>
        <dt className="text-gray-500">Provider request id</dt><dd className="font-mono">{item.providerRequestId || "—"}</dd>
        <dt className="text-gray-500">First sent</dt><dd>{fmtTime(item.firstDispatchedAt)} ({fmtAge(item.ageMinutes)} ago)</dd>
        <dt className="text-gray-500">Attempts / resubmits</dt><dd>{item.attemptCount} / {item.resubmitCount}</dd>
        <dt className="text-gray-500">Status checks</dt><dd>{item.tsq?.count || 0} · last {item.tsq?.lastState || "—"} at {fmtTime(item.tsq?.lastAt)}</dd>
      </dl>

      <div className="text-sm">
        <div className="text-gray-500 mb-1">Why it needs a decision</div>
        <div className="bg-amber-50 border border-amber-200 rounded p-3">{item.reason || item.failureCode || "—"}</div>
      </div>

      <div className="text-sm">
        <div className="text-gray-500 mb-1">Name check (no M-Pesa lookup exists before payout)</div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
          <span>Ours: <b>{item.nameCheck?.recipientName || "—"}</b></span>
          <span>Paid to: <b>{item.nameCheck?.providerReceiverName || "—"}</b></span>
        </div>
      </div>

      {item.resolution && (
        <div className="text-sm bg-gray-50 border rounded p-3">
          Resolved <b>{item.resolution.outcome}</b> by {item.resolution.by} on {fmtTime(item.resolution.at)}
          <div className="text-gray-600 mt-1">“{item.resolution.note}”</div>
        </div>
      )}

      {flash && (
        <div className={`text-sm rounded p-3 ${flash.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>{flash.text}</div>
      )}

      {item.canResolve && !mode && (
        <div className="flex gap-3">
          <button onClick={() => setMode("PAID")} className="px-4 py-2 rounded bg-green-600 text-white text-sm hover:bg-green-700">Mark as paid</button>
          <button onClick={() => setMode("NOT_PAID")} className="px-4 py-2 rounded bg-red-600 text-white text-sm hover:bg-red-700">Mark as not paid</button>
        </div>
      )}
      {!item.canResolve && item.status === "RECONCILIATION_REQUIRED" && (
        <p className="text-sm text-gray-500">Your role can view this item but not resolve it. A super admin or finance admin must decide.</p>
      )}

      {mode && (
        <div className="space-y-3 border-t pt-4">
          <h3 className="font-medium">{mode === "PAID" ? "Confirm payment was made" : "Confirm payment was NOT made"}</h3>
          {mode === "PAID" && (
            <label className="block text-sm">
              M-Pesa / provider reference <span className="text-red-600">*</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. SJK1AB2CD3"
                className="mt-1 w-full border rounded px-3 py-2 font-mono" />
            </label>
          )}
          <label className="block text-sm">
            How you confirmed it <span className="text-red-600">*</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder={mode === "PAID" ? "e.g. Matched on Eclectics statement 5 Oct, receipt SJK1AB2CD3" : "e.g. Eclectics confirmed status 55 by email, nothing posted"}
              className="mt-1 w-full border rounded px-3 py-2" />
            {!noteOk && <span className="text-xs text-gray-500">At least 10 characters.</span>}
          </label>
          <div className="flex gap-3">
            <button disabled={!noteOk || !refOk || saving} onClick={submit}
              className={`px-4 py-2 rounded text-white text-sm disabled:opacity-40 ${mode === "PAID" ? "bg-green-600" : "bg-red-600"}`}>
              {saving ? "Saving…" : mode === "PAID" ? "Confirm paid" : "Confirm not paid"}
            </button>
            <button onClick={() => setMode(null)} className="px-4 py-2 rounded border text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function QueueTable({ rows, onSelect, selectedId, emptyText }) {
  if (!rows.length) return <p className="text-sm text-gray-500 px-1">{emptyText}</p>;
  return (
    <div className="overflow-hidden bg-white border rounded-lg">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="text-left px-3 py-2 w-44">Item</th>
            <th className="text-left px-3 py-2 w-24">Amount</th>
            <th className="text-left px-3 py-2 w-20">Waiting</th>
            <th className="text-left px-3 py-2 w-24">Last check</th>
            <th className="text-left px-3 py-2">Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onSelect(r.id)}
              className={`border-t cursor-pointer hover:bg-gray-50 ${selectedId === r.id ? "bg-blue-50" : ""}`}>
              <td className="px-3 py-2 font-mono text-xs whitespace-nowrap truncate" title={r.itemReference}>{r.itemReference}</td>
              <td className="px-3 py-2 whitespace-nowrap">{fmtAmount(r.destinationAmount, r.destinationCurrency)}</td>
              <td className={`px-3 py-2 whitespace-nowrap ${r.ageMinutes >= 24 * 60 ? "text-red-700 font-semibold" : ""}`}>{fmtAge(r.ageMinutes)}</td>
              <td className="px-3 py-2">{r.tsq?.lastState || "—"}</td>
              <td className="px-3 py-2 text-gray-600 truncate" title={r.reason || ""}>{r.reason || r.failureCode || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CockpitReconciliation() {
  const [queue, setQueue] = useState({ needsHuman: [], resolvingAutomatically: [], canResolve: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setError(null);
    call("/reconciliation")
      .then(({ status, body }) => (body.ok ? setQueue(body) : setError(body.error || `HTTP ${status}`)))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">⚖️ Payout Reconciliation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Payouts whose outcome the system could not prove. Confirm each against the provider statement before deciding.
            {!queue.canResolve && " Your role can view but not resolve."}
          </p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded border text-sm">Refresh</button>
      </div>

      <ProviderStatus />

      {error && <div className="bg-red-50 text-red-800 rounded p-3 text-sm">{error}</div>}
      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="space-y-6 xl:col-span-3 min-w-0">
            <section className="space-y-2">
              <h2 className="font-semibold">Needs a decision ({queue.needsHuman.length})</h2>
              <QueueTable rows={queue.needsHuman} onSelect={setSelectedId} selectedId={selectedId}
                emptyText="Nothing waiting. Every payout has a confirmed outcome." />
            </section>
            <section className="space-y-2">
              <h2 className="font-semibold text-gray-700">Resolving automatically ({queue.resolvingAutomatically.length})</h2>
              <p className="text-xs text-gray-500">Status unclear; the system is querying the provider. No action needed unless they move above.</p>
              <QueueTable rows={queue.resolvingAutomatically} onSelect={setSelectedId} selectedId={selectedId}
                emptyText="None." />
            </section>
          </div>
          <div className="xl:col-span-2 min-w-0">
            {selectedId
              ? <ItemDetail id={selectedId} onClose={() => setSelectedId(null)} onResolved={load} />
              : <div className="text-sm text-gray-500 border border-dashed rounded-lg p-6">Select an item to see its details.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
